import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import {Table, TableColResizeEvent, TableLazyLoadEvent} from "primeng/table";
import { MedNotificationService } from "@mk/med-notification";
import { MedOptionType } from "@mk/med-ui";
import { PAGINATION_OPTIONS } from "@app/components/supplies/supplies.component";
import { DataService } from "@app/services/data.service";
import { OrganizationApiService } from "@app/services/organization-api.service";
import { OrganizationViewModel } from "@app/models/organization.model";
import { Router } from "@angular/router";
import {FilterService} from "@app/services/filter.service";
import {DatePipe} from "@angular/common";

@Component({
  selector: "app-organizations",
  templateUrl: "./organizations.component.html",
  styleUrls: ["/organizations.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class OrganizationsComponent implements OnInit, OnDestroy {

  constructor(
      private readonly notificationService: MedNotificationService,
      private readonly organizationApiService: OrganizationApiService,
      private readonly router: Router,
      public filter: FilterService,
      public dataService: DataService,
  ) {
  }

  organizationList: any = [];

    @ViewChild("dt") dt: Table | undefined;
    tabHeader: {
        label: string;
        key: string;
        width: string;
    }[] = tabHeaderOrganizations;
    rows = 0;
    totalRows = 0;
    pageSize = 10;
    selectedPageNumber = 1;
    filterActive = true;
    filterField: string | string[] | null | undefined = "";
    tableState: TableLazyLoadEvent = {};
    sortOrder: number | undefined | null = null;
    filtersSubscriber: any;

    readonly paginationOptions: MedOptionType<number>[] = PAGINATION_OPTIONS;

    loadData($event: TableLazyLoadEvent, resetState = false) {
      this.tableState = $event;
      this.filterField = $event.sortField;
      if (resetState) {
        if ($event.sortField) {
          this.filterActive = this.sortOrder !== -1;
        } else {
          this.filterActive = false;
        }
      }
      if (this.sortOrder === -1 && resetState) {
        this.sortOrder = null;
        this.dt?.reset();
      } else {
        this.getOrganizations(this.selectedPageNumber, $event.sortOrder === 1 ? "asc" : "desc", $event.sortField?.toString());
        this.sortOrder = $event.sortOrder;
      }
    }

    ngOnInit(): void {
      if ("DOZ" === this.dataService.userRoleValue.getValue() || "MOZ" === this.dataService.userRoleValue.getValue()) {
        this.router.navigate(["/"]);
      }
      this.loadColumnOrder();
      this.filtersSubscriber = this.filter.organizationsMZUSubject.subscribe(() => {
        this.getOrganizations(this.selectedPageNumber);
      });
    }

    ngOnDestroy(): void {
      this.filtersSubscriber.unsubscribe();
    }

    saveColumnOrder() {
      localStorage.setItem("orgsColumnOrder", JSON.stringify(this.tabHeader));
    }

    loadColumnOrder() {
      const savedOrder = localStorage.getItem("orgsColumnOrder");
      if (savedOrder) {
        this.tabHeader = JSON.parse(savedOrder);
      }
    }

    getOrganizations(
      page = 0,
      order = "asc",
      field = "id",
    ): void {
      let params = new HttpParams();
      this.selectedPageNumber = page;
      const sortFieldAndOrder = `${field},${order}`;
      params = params.append("page", this.selectedPageNumber - 1);
      params = params.append("size", this.pageSize);
      params = params.append("sort", sortFieldAndOrder);

      const currentYear = new Date().getFullYear();
      this.organizationApiService.getOrganizations(currentYear, this.dataService.userRoleValue.getValue().toLowerCase(), this.addFilterParams(params)).subscribe(data => {
        console.log(data)
        this.totalRows = data.totalElements;
        this.organizationList = data.content;
      });
    }

    addFilterParams(params: HttpParams): HttpParams {
      const subscriber = this.filter.organizationsMZUSubject.subscribe(data => {
        let counter = 0;
        if (data.directions.length) {
          params = params.append(`conditions[${counter}].fieldName`, "directions");
          params = params.append(`conditions[${counter}].value`, data.directions[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.district.length) {
          params = params.append(`conditions[${counter}].fieldName`, "district");
          params = params.append(`conditions[${counter}].value`, data.district[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.name.length) {
          params = params.append(`conditions[${counter}].fieldName`, "name");
          params = params.append(`conditions[${counter}].value`, data.name[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.edrpou.length) {
          params = params.append(`conditions[${counter}].fieldName`, "edrpou");
          params = params.append(`conditions[${counter}].value`, data.edrpou[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.shortName.length) {
          params = params.append(`conditions[${counter}].fieldName`, "shortName");
          params = params.append(`conditions[${counter}].value`, data.shortName[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.address.length) {
          params = params.append(`conditions[${counter}].fieldName`, "address");
          params = params.append(`conditions[${counter}].value`, data.address[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.email.length) {
          params = params.append(`conditions[${counter}].fieldName`, "email");
          params = params.append(`conditions[${counter}].value`, data.email[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.manager.length) {
          params = params.append(`conditions[${counter}].fieldName`, "manager");
          params = params.append(`conditions[${counter}].value`, data.manager[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.responsiblePersons.length) {
          params = params.append(`conditions[${counter}].fieldName`, "responsiblePersons");
          params = params.append(`conditions[${counter}].value`, data.responsiblePersons[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.level.length) {
          params = params.append(`conditions[${counter}].fieldName`, "level");
          params = params.append(`conditions[${counter}].value`, data.level[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.type.length) {
          params = params.append(`conditions[${counter}].fieldName`, "type");
          params = params.append(`conditions[${counter}].value`, data.type[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
        if (data.status.length) {
          params = params.append(`conditions[${counter}].fieldName`, "status");
          params = params.append(`conditions[${counter}].value`, data.status[0]);
          params = params.append(`conditions[${counter}].operator`, "CONTAINS");
          counter++;
        }
      });
      subscriber.unsubscribe();
        return params;
    }

    getFirstShowedRowIndex() {
      return this.selectedPageNumber === 1 ? 0 : (this.selectedPageNumber - 1) * this.pageSize;
    }

    copyToClipboard(productElement: string): void {
      const selBox = document.createElement("textarea");
      selBox.style.position = "fixed";
      selBox.style.left = "0";
      selBox.style.top = "0";
      selBox.style.opacity = "0";
      selBox.value = productElement;
      document.body.appendChild(selBox);
      selBox.focus();
      selBox.select();
      document.execCommand("copy");
      document.body.removeChild(selBox);
      this.notificationService.success(`${productElement} was copied to the clipboard`);
    }

    pageChange(pageNumber: number) {
      this.selectedPageNumber = pageNumber;
      this.loadData(this.tableState);
    }

    pageSizeChanged(pageSize: number) {
      this.pageSize = pageSize;
      this.selectedPageNumber = 1;
      this.loadData(this.tableState);
    }

  isVisibleFilters(): boolean {
    return this.filter.organizationsFilter;
  }

  handleColumnResize($event: TableColResizeEvent) {
    this.tabHeader.forEach(element => {
      if (element.label === $event.element.innerText.trim()) {
        element.width = $event.element.clientWidth + "px";
      }
    })
    this.saveColumnOrder();
  }

  getWithForColumn(key: string) {
    let width: string = "initial";
    this.tabHeader.filter(element => element.key === key).forEach(element => {
      width = element.width;
    })
    return width;
  }

  getPathStatusImg(rowElement: string): string {
    let color: string;
    if (rowElement === "ACTIVE") {
      color = "green";
    } else {
      color = "red";
    }
    return `assets/icons/table/Statys_indicator_${color}.png`;
  }
}

export const tabHeaderOrganizations = [
  { key: "directions", label: "Напрям", width: "initial" },
  { key: "district", label: "Область", width: "initial" },
  { key: "name", label: "Повна назва організації", width: "initial" },
  { key: "edrpou", label: "ЄДРПОУ", width: "initial" },
  { key: "shortName", label: "Коротка назва", width: "initial" },
  { key: "address", label: "Фактична адреса", width: "initial" },
  { key: "email", label: "Email закладу", width: "initial" },
  { key: "manager", label: "Керівник та контакти", width: "initial" },
  { key: "responsiblePersons", label: "Відповідальні особи та контакти", width: "initial" },
  { key: "subordinationId", label: "Рівень", width: "initial" },
  { key: "type", label: "Тип", width: "initial" },
  { key: "status", label: "Статус", width: "initial" },
];
